import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import SmartGreeting from "../../components/SmartGreeting";
import HealthRing from "../../components/HealthRing";
import "./dashboard.css";

function hashToHSL(str = "") {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
        h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
    }
    return `hsl(${Math.abs(h) % 360}, 55%, 42%)`;
}

function initialsOf(s = "") {
    return s.split(/\s+/).filter(Boolean).slice(0, 2)
            .map((w) => w[0].toUpperCase()).join("");
}

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    const days = Math.floor(diff / 86400);
    if (days < 7) return `${days} g fa`;
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}

const STATUS_LABELS = {
    aperto: "Aperto",
    in_lavorazione: "In lavorazione",
    in_attesa: "In attesa",
    chiuso: "Chiuso",
};

function hintFor(color, count) {
    if (color === "green" && count === 0) return "Tutto sotto controllo.";
    if (color === "amber") return "Alcune pratiche stanno maturando: ti aggiorniamo a breve.";
    if (color === "red") return "Ci sono pratiche in sospeso che stiamo seguendo.";
    return "";
}

export default function ClientDashboard() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/portal/dashboard/today");
                setData(r.data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="enl-dash"><div className="enl-empty">Caricamento…</div></div>;
    if (!data) return null;

    const { user, client, health, novita = [], my_open_tickets = [] } = data;
    const color = health?.color || "green";
    const openN = health?.open_tickets || 0;

    return (
        <div className="enl-dash" data-testid="client-dashboard-root">
            <SmartGreeting
                user={{ name: user?.first_name || user?.name }}
                metrics={{ ticket_open: openN }}
                onNumberClick={() => nav("/portal/ticket")}
            />

            <section className="enl-client-hero" data-testid="client-dashboard-hero" style={{ marginTop: 24 }}>
                <div className="ring-wrap" data-testid="client-dashboard-ring">
                    <HealthRing color={color} size={168} stroke={8} ariaLabel={`Stato ${color}`}>
                        <div style={{
                            width: "100%", height: "100%", borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: client?.has_logo ? "#F1F5F9" : hashToHSL(client?.company_name),
                            color: "#FFFFFF", fontFamily: "Archivo", fontWeight: 700,
                            fontSize: 36,
                        }}>
                            {initialsOf(client?.company_name)}
                        </div>
                    </HealthRing>
                </div>
                <div>
                    <h2 className="company" data-testid="client-dashboard-company">
                        {client?.company_name}
                    </h2>
                    <div style={{
                        fontFamily: "IBM Plex Mono, monospace",
                        fontSize: 11, letterSpacing: "0.12em",
                        color: "#64748B", textTransform: "uppercase",
                    }}>
                        Il tuo stato di salute
                    </div>
                    <p className="hint" data-testid="client-dashboard-hint">
                        {hintFor(color, openN)}
                    </p>
                    {openN > 0 && (
                        <a href="/portal/ticket"
                           onClick={(e) => { e.preventDefault(); nav("/portal/ticket"); }}
                           className="cta-btn"
                           data-testid="client-dashboard-cta">
                            Vedi i miei ticket →
                        </a>
                    )}
                </div>
            </section>

            <div className="enl-widgets-grid">
                <div className="enl-widget" data-testid="today-novita-panel">
                    <h3>Novità per te</h3>
                    {novita.length === 0 && (
                        <div className="enl-empty" data-testid="novita-empty">
                            Nessuna novità recente.
                        </div>
                    )}
                    <ul className="enl-widget-list">
                        {novita.slice(0, 5).map((n) => (
                            <li key={n.id} data-testid={`novita-item-${n.id}`}>
                                {n.body}
                                <span className="meta">{timeAgo(n.created_at)}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="enl-widget" data-testid="today-tickets-panel">
                    <h3>I miei ticket aperti</h3>
                    {my_open_tickets.length === 0 && (
                        <div className="enl-empty" data-testid="tickets-empty">
                            Nessun ticket aperto.
                        </div>
                    )}
                    <ul className="enl-widget-list">
                        {my_open_tickets.slice(0, 5).map((t) => (
                            <li key={t.id} data-testid={`ticket-item-${t.id}`}>
                                <a href={`/portal/ticket/${t.id}`}
                                   onClick={(e) => { e.preventDefault(); nav(`/portal/ticket/${t.id}`); }}>
                                    {t.title}
                                </a>
                                <span className="meta">
                                    {STATUS_LABELS[t.status] || t.status}
                                    {t.category ? ` · ${t.category}` : ""}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <a href="/portal/ticket"
                       onClick={(e) => { e.preventDefault(); nav("/portal/ticket"); }}
                       className="enl-widget-see-all"
                       data-testid="today-tickets-see-all">
                        Vedi tutti →
                    </a>
                </div>
            </div>

            <div className="enl-radar" data-testid="today-scadenze-placeholder" style={{ marginTop: 24 }}>
                <div className="enl-radar-glyph" aria-hidden>
                    <svg width="48" height="48" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="24" fill="none"
                                stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 3" />
                        <circle cx="28" cy="28" r="14" fill="none"
                                stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 3" />
                        <line x1="28" y1="28" x2="52" y2="28"
                              stroke="#94A3B8" strokeWidth="1.5" opacity="0.5" />
                    </svg>
                    <div className="ping" />
                </div>
                <div className="enl-radar-title">
                    Le tue prossime scadenze appariranno qui.
                </div>
                <div className="enl-radar-sub">Feature Scadenze in arrivo</div>
            </div>
        </div>
    );
}
